// using the /max instace to add landlines verification
import { parsePhoneNumberFromString as parsePhoneNumber } from 'libphonenumber-js/max';
import { each, get, isDate, isEmpty, isNumber, isString, map, replace, set } from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import { validateMobileNumber } from 'ui-utils';

const formatMilitaryToAmPm = (timeValue: unknown) => {
  if (!timeValue) return '';
  const timeString = String(timeValue);
  const baseDate = '1990-01-01';
  const patternMilitary = 'YYYY-MM-DD hh:mm';
  const patternAmPm = 'YYYY-MM-DD hh:mm a';
  const dateMoment = moment(`${baseDate} ${timeString}`, patternMilitary);
  const dateInAmPm = moment(dateMoment).format(patternAmPm);

  // 1990-01-01 hh:mm am => hh:mm am
  const normalizedDate = dateInAmPm.slice(baseDate.length).trim();
  return normalizedDate;
};

export function isFutureDate(value: string) {
  if (value && value.length === 10 && moment(value, 'MM/DD/YYYY').isAfter(moment().startOf('day'))) {
    return true;
  }

  return false;
}

const join = (rules: any) => (value: any, data: any) =>
  rules.map((rule: any) => rule(value, data)).filter((error: any) => !!error)[0];

export function email(value: any) {
  if (!isEmpty(value) && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value)) {
    return 'Invalid email address';
  }
  return false;
}

export function required(value: any) {
  // WORKAROUND numbers and dates return true for isEmpty
  // https://github.com/lodash/lodash/issues/483
  let newValue = value;
  if (isString(value)) {
    newValue = value.trim();
  }

  if (isEmpty(newValue) && !isNumber(newValue) && !isDate(newValue)) {
    return 'Field required';
  }
  return false;
}

export function match(password: any) {
  return (value: any) => {
    if (value !== password) {
      return 'Passwords don’t match';
    }
    return false;
  };
}

// TODO unify `isValidDateCustom` and `isValidDate` methods and fix naming:
// `isValidDate` returns `false` for valid dates, while
// `isValidDateCustom` returns `true` for valid dates
export function isValidDateCustom(value: any) {
  if (!value || isEmpty(value) || !isString(value)) return true;

  const onlyNumbers = value.replace(/[^\d]/g, '');
  let equalSequenceTest = `(0{${onlyNumbers.length}})`;

  for (let i = 1; i < 10; i += 1) {
    equalSequenceTest += `|(${i}{${onlyNumbers.length}})`;
  }

  if (
    value &&
    value.length === 10 &&
    !onlyNumbers.match(`^${equalSequenceTest}$`) &&
    moment(value, 'MM/DD/YYYY').isValid() &&
    moment(value, 'MM/DD/YYYY').isAfter(moment().year(1900))
  ) {
    return true;
  }

  return false;
}

export function isValidDate(value: any) {
  if (!value || isEmpty(value) || !isString(value)) return false;

  const onlyNumbers = value.replace(/[^\d]/g, '');
  let equalSequenceTest = `(0{${onlyNumbers.length}})`;

  for (let i = 1; i < 10; i += 1) {
    equalSequenceTest += `|(${i}{${onlyNumbers.length}})`;
  }

  if (
    value &&
    value.length === 10 &&
    !onlyNumbers.match(`^${equalSequenceTest}$`) &&
    moment(value, 'MM/DD/YYYY').isValid() &&
    moment(value, 'MM/DD/YYYY').isAfter(moment().year(1900))
  ) {
    return false;
  }

  return 'Not a valid date';
}

export function createValidator(rules: any) {
  return (formData: any, props: any) => {
    const data = formData.toJS();
    const errors = {};

    // this code can be improved to be recursive and support more levels of nested collections
    Object.keys(rules).forEach((key) => {
      if (get(rules[key], 'type') === 'collection') {
        const fields = get(rules[key], 'fields');
        const nestedData = get(data, key);

        map(nestedData, (item, index) => {
          const nestedErrors: any = {};

          Object.keys(fields).forEach((fieldKey: string) => {
            const validators = get(fields[fieldKey], 'validators', fields[fieldKey]);
            const rule: any = join([].concat(validators));
            const error = rule(get(item, fieldKey), item, data, props);

            if (error) nestedErrors[fieldKey] = error;
          });

          if (Object.keys(nestedErrors).length > 0) set(errors, `${key}[${index}]`, nestedErrors);
        });
      } else {
        // option to pass a key dependency to enable/disable validator
        const keyEnabled = get(rules[key], 'keyEnabled');
        if (!keyEnabled || get(data, keyEnabled)) {
          // run validators
          const validators = get(rules[key], 'validators', rules[key]);
          const rule: any = join([].concat(validators));
          const error = rule(get(data, key), data, data, props);
          if (error) {
            set(errors, key, error);
          }
        }
      }
    });

    return errors;
  };
}

export function extractURLs(value: string) {
  const splitString = value.split(/(\s)/g);
  const urls = splitString.filter((v: any) => v.startsWith('http'));
  let string = value;
  each(urls, (url) => {
    string = string.replace(url, '');
  });
  return {
    string,
    urls,
  };
}

export function getValidationSchema(name: string, type: string, validations: any = {}, initialSchema?: any) {
  let schema = initialSchema || Yup.string();
  if (type === 'ssn') {
    schema = Yup.string().matches(
      /^$|^\d{3}-\d{2}-\d{4}$/,
      'Invalid social security number. Please use the format: XXX-XX-XXXX',
    );
  } else if (type === 'zipcode') {
    schema = Yup.string().matches(/^$|^\d{5}$/, 'Invalid Zip Code. Please use the format: 00000');
  } else if (type === 'phone_number') {
    schema = Yup.string().test(
      'is-valid-phone-number',
      'Invalid phone number. Please use the format: (555) 555-5555',
      (value) => !validateMobileNumber(value),
    );
  } else if (type === 'date') {
    schema = Yup.string()
      // Empty dates default to `01-01-2000` to bypass not required validation
      // https://github.com/jquense/yup/issues/58#issuecomment-231746266
      .transform((value) => (!value && !validations.required ? '01-01-2000' : value))
      .matches(
        /^$|^(((0)[0-9])|((1)[0-2]))-([0-2][0-9]|(3)[0-1])-\d{4}$/,
        'Invalid date. Please use the format: mm-dd-yyyy',
      )
      .test('date', 'Cannot be in the future', (value: any) => !isFutureDate(value))
      .test('date', 'Invalid date', (value) => isValidDateCustom(value));
  } else if (type === 'email') {
    schema = Yup.string()
      .email('Invalid email')
      .test('email', 'Invalid email', (value) => !email(value));
  } else if (type === 'short_text' || type === 'long_text') {
    const maxLength = parseInt(`${validations.maxLength || '0'}`, 10);

    if (get(validations, 'type') === 'number' && maxLength) {
      schema = Yup.string()
        .matches(/^[0-9]+$/, 'Must be only digits')
        .min(maxLength, `Must be exactly ${maxLength} digits`)
        .max(maxLength, `Must be exactly ${maxLength} digits`);
    } else if (maxLength) {
      schema = Yup.string()
        .max(maxLength, `Must not exceed ${maxLength} character${maxLength > 1 ? 's' : ''} limit`)
        .trim();
    } else {
      schema = Yup.string().trim();
    }
  } else if (type === 'integer') {
    const notIntegerMessage = 'Must be an integer number';

    const defaultIntervalError =
      isNumber(validations.minimum) && isNumber(validations.maximum)
        ? `Must be between ${validations.minimum} and ${validations.maximum}`
        : '';

    if (defaultIntervalError) {
      schema = Yup.number()
        .integer(notIntegerMessage)
        .min(validations.minimum, defaultIntervalError)
        .max(validations.maximum, defaultIntervalError);
    } else if (isNumber(validations.minimum)) {
      schema = Yup.number()
        .integer(notIntegerMessage)
        .min(validations.minimum, `Must be equal or greater than ${validations.minimum}`);
    } else if (isNumber(validations.maximum)) {
      schema = Yup.number()
        .integer(notIntegerMessage)
        .max(validations.maximum, `Must be equal or lower than ${validations.maximum}`);
    }
  } else if (type === 'time') {
    const regexMilitaryHour = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])$/;
    const maxTime = regexMilitaryHour.test(`${validations.maximum || ''}`) ? `${validations.maximum}` : '23:59';
    const minTime = regexMilitaryHour.test(`${validations.minimum || ''}`) ? `${validations.minimum}` : '00:00';
    const maxTimeAmPm = formatMilitaryToAmPm(maxTime).toUpperCase();
    const minTimeAmPm = formatMilitaryToAmPm(minTime).toUpperCase();
    // hh:mm a
    const isValidTimeFormat = (timeValue: string) => {
      if (!validations.required) {
        return /^ [ap]m$/gi.test(timeValue) || /^(0?[1-9]|1[0-2]):[0-5][0-9] [ap]m$/gi.test(timeValue);
      }
      return /^(0?[1-9]|1[0-2]):[0-5][0-9] [ap]m$/gi.test(timeValue);
    };

    schema = Yup.string()
      .test('is-valid-format-time', 'Invalid time. Please use the format: hh:mm', (value) =>
        isValidTimeFormat(String(value)),
      )
      .test('is-valid-time', `Must be between ${minTimeAmPm} and ${maxTimeAmPm}`, (value) => {
        const baseDate = '1990-01-01';
        const pattern = 'YYYY-MM-DD hh:mm a';
        const currentValueMoment = moment(`${baseDate} ${String(value)}`, pattern);
        const minDate = moment(`${baseDate} ${minTime}`, pattern);
        const maxDate = moment(`${baseDate} ${maxTime}`, pattern);
        return currentValueMoment.isSameOrAfter(minDate) && currentValueMoment.isSameOrBefore(maxDate);
      });
  } else if (type === 'year') {
    const maxYear = parseInt(replace(`${validations.maximum || ''}`, /[^\d]/g, '') || '2100', 10);
    const minYear = parseInt(replace(`${validations.minimum || ''}`, /[^\d]/g, '') || '1900', 10);

    const isValidYear = (year: number, fallback: number) => (year >= 1900 && year <= 2100 ? year : fallback);

    const defaultIntervalError = `Must be between ${isValidYear(minYear, 1900)} and ${isValidYear(maxYear, 2100)}`;

    schema = Yup.number()
      .min(isValidYear(minYear, 1900), defaultIntervalError)
      .max(isValidYear(maxYear, 2100), defaultIntervalError);
  }
  return {
    [name]: validations.required ? schema.required('Required') : schema,
  };
}

export function validatePhoneNumber(value: String, country: any = false, invalidPhoneAlert: any = false) {
  if (!value || isEmpty(value) || !isString(value)) return false;

  let phoneNumber;
  if (country) {
    phoneNumber = parsePhoneNumber(value, country);
  } else {
    // Default to US > BR
    phoneNumber = parsePhoneNumber(value, 'US');
    if (!phoneNumber || !phoneNumber.isValid()) {
      phoneNumber = parsePhoneNumber(value, 'BR');
    }
  }
  return phoneNumber && phoneNumber.isValid() ? false : invalidPhoneAlert || 'Not a valid phone number';
}
